import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Player } from "../types";
import { BuzzerState } from "./BuzzerStatus";
import "./Scoreboard.css";

const TEAM_COLORS = ['#1565c0', '#b71c1c', '#2e7d32'];

interface ScoreboardProps {
  currentValue: number | null;
  players: Player[];
  stats: boolean;
  updateScore: (i: number, clueValue: number, correct: boolean) => void;
  wagering: boolean;
  buzzer?: BuzzerState | null;
  buzzerCount?: number[];
  rosters?: string[][];
  captains?: (string | null)[];
}

function Scoreboard(props: ScoreboardProps) {
  const { currentValue, players, stats, updateScore, wagering, buzzer, buzzerCount, rosters, captains } = props;
  const [wagers, setWagers] = useState<string[]>(() => players.map(() => ""));
  // qrTeam: which team's QR modal is open (-1 = none)
  const [qrTeam, setQrTeam] = useState<number>(-1);

  const origin = window.location.origin;

  function renderPlayer(player: Player, i: number) {
    const scoreString = player.score >= 0 ? `$${player.score}` : `-$${-player.score}`;
    const clueValue: number | null =
      currentValue === null
        ? null
        : wagering === false
        ? currentValue
        : parseInt(wagers[i]) || 0;

    const isLockedOut = buzzer?.wrongTeams.includes(i) ?? false;
    const hasBuzzed = buzzer?.activeBuzz?.teamIndex === i;
    const teamColor = TEAM_COLORS[i] ?? '#1565c0';
    const teamRoster = rosters?.[i] ?? [];
    const teamCaptain = captains?.[i] ?? null;

    return (
      <div key={i} className="podium">
        <div className="podium-score">{scoreString}</div>
        <div className="podium-name">{player.name}</div>

        {teamRoster.length > 0 && (
          <ul className="podium-roster" style={{ borderColor: teamColor }}>
            {teamRoster.map((name) => (
              <li key={name} className={teamCaptain === name ? 'podium-roster-captain' : ''}>
                {name}
                {teamCaptain === name && <span className="podium-roster-star"> ★</span>}
              </li>
            ))}
          </ul>
        )}

        {/* Buzz status badge */}
        {hasBuzzed && (
          <div className="buzz-badge buzz-badge-active" style={{ background: teamColor }}>
            BUZZED IN
          </div>
        )}
        {isLockedOut && !hasBuzzed && (
          <div className="buzz-badge buzz-badge-locked">LOCKED OUT</div>
        )}

        {stats && (
          <div className="stats">
            <hr />
            <div>Correct: {player.correct}</div>
            <div>Incorrect: {player.incorrect}</div>
          </div>
        )}

        {/* Buzzer count badge + QR button */}
        <div className="buzzer-count-row" onClick={() => setQrTeam(i)}>
          <span className="buzzer-count-icon">📱</span>
          <span className="buzzer-count-num">{buzzerCount?.[i] ?? 0}</span>
          <span className="buzzer-count-label">buzzer{(buzzerCount?.[i] ?? 0) !== 1 ? 's' : ''}</span>
        </div>
      </div>
    );
  }

  function changeWager(i: number, wager: string) {
    setWagers(wagers.map((w, idx) => (idx === i ? wager : w)));
  }

  function updateScoreboardScore(i: number, clueValue: number, correct: boolean) {
    setWagers(wagers.map((w, idx) => (idx === i ? "" : w)));
    updateScore(i, clueValue, correct);
  }

  return (
    <>
      <div className="scoreboard">
        {players.map((player, i) => renderPlayer(player, i))}
      </div>

      {/* QR Code modal */}
      {qrTeam >= 0 && (
        <div className="qr-overlay" onClick={() => setQrTeam(-1)}>
          <div className="qr-modal" onClick={e => e.stopPropagation()}>
            <div className="qr-team-name" style={{ color: TEAM_COLORS[qrTeam] ?? '#1565c0' }}>
              {players[qrTeam]?.name ?? `Team ${qrTeam + 1}`}
            </div>
            <div className="qr-instruction">Scan to buzz in on your phone</div>
            <div className="qr-code-wrap">
              <QRCodeSVG
                value={`${origin}/buzz/${qrTeam}`}
                size={Math.min(window.innerWidth, window.innerHeight) * 0.55}
                bgColor="#ffffff"
                fgColor="#000000"
                level="M"
              />
            </div>
            <div className="qr-url">{origin}/buzz/{qrTeam}</div>
            <button className="qr-close-btn" onClick={() => setQrTeam(-1)}>Close</button>
          </div>
        </div>
      )}
    </>
  );
}

export default Scoreboard;
